'use client';

import React, { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import CategoryIcon from '@/components/CategoryIcon';
import { ALL_AUTOMOTIVE_CATEGORIES } from '@/lib/all-categories';

export interface CategoryCarouselRailProps {
  country?: string;
  activeCategory?: string;
  onSelectCategory?: (categoryId: string) => void;
  className?: string;
  showSectionHeader?: boolean;
}

export default function CategoryCarouselRail({
  country = 'gh',
  activeCategory,
  onSelectCategory,
  className = '',
  showSectionHeader = true,
}: CategoryCarouselRailProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const countrySlug = (country || 'gh').toLowerCase();

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll, { passive: true });
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = direction === 'left' ? -320 : 320;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <section className={`w-full ${className}`}>
      {showSectionHeader && (
        <div className="flex items-center justify-between gap-3 pb-3 mb-3 border-b border-slate-200/80 dark:border-slate-800/80">
          <div>
            <h2 className="font-display font-extrabold text-lg sm:text-xl text-[#060D1E] dark:text-white tracking-tight">
              Shop by Category
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Browse all 20 automotive mechanical, electrical, and body systems
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              href={`/${countrySlug}/shop`}
              className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1 group mr-1 sm:mr-2"
            >
              <span>Explore All</span>
              <ArrowRight className="size-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            {/* Scroll navigation arrows */}
            <div className="hidden sm:flex items-center gap-1">
              <button
                type="button"
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                aria-label="Scroll categories left"
                className={`p-1.5 rounded-full border transition-all ${
                  canScrollLeft
                    ? 'bg-white dark:bg-[#0E1526] border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-xs cursor-pointer'
                    : 'bg-transparent border-slate-200/40 dark:border-slate-800/40 text-slate-300 dark:text-slate-700 cursor-not-allowed opacity-50'
                }`}
              >
                <ChevronLeft className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                aria-label="Scroll categories right"
                className={`p-1.5 rounded-full border transition-all ${
                  canScrollRight
                    ? 'bg-white dark:bg-[#0E1526] border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-xs cursor-pointer'
                    : 'bg-transparent border-slate-200/40 dark:border-slate-800/40 text-slate-300 dark:text-slate-700 cursor-not-allowed opacity-50'
                }`}
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Circular Carousel Track */}
      <div className="relative group/carousel">
        <div
          ref={scrollRef}
          className="flex items-start gap-4 sm:gap-6 overflow-x-auto pb-2 scrollbar-none snap-x snap-mandatory scroll-smooth"
        >
          {ALL_AUTOMOTIVE_CATEGORIES.map((cat) => {
            const isActive = Boolean(activeCategory && activeCategory.toLowerCase() === cat.id.toLowerCase());
            const displayName = cat.name.split('&')[0].trim();

            const itemContent = (
              <div className="flex flex-col items-center group cursor-pointer text-center select-none snap-start shrink-0">
                <CategoryIcon
                  category={cat.id}
                  variant="circle"
                  isActive={isActive}
                />

                {/* Title & Code Underneath */}
                <div className="mt-2 flex flex-col items-center max-w-[80px] sm:max-w-[90px]">
                  <span
                    className={`text-xs font-bold transition-colors line-clamp-1 leading-snug ${
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 font-extrabold'
                        : 'text-slate-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                    }`}
                  >
                    {displayName}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                    {cat.code}
                  </span>
                </div>
              </div>
            );

            if (onSelectCategory) {
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => onSelectCategory(cat.id)}
                  aria-label={`Filter by ${cat.name}`}
                  className="bg-transparent border-0 p-0 text-left focus:outline-none"
                >
                  {itemContent}
                </button>
              );
            }

            return (
              <Link
                key={cat.id}
                href={`/${countrySlug}/category/${encodeURIComponent(cat.id)}`}
                title={`Shop verified ${cat.name} parts`}
              >
                {itemContent}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
